import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  paymentMethods,
  qualifiesForFreeShipping,
} from "../../utils/global_const";
import CreditCardsCheckout from "../../components/stripe/CreditCardsCheckout";
import { useContext, useEffect, useState } from "react";
import CreditCardButton from "../stripe/CreditCardButton";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ProductContext } from "../../context/ProductContext";

const stripePromise = loadStripe(
  "pk_test_51QI78aHyD45BjVAFFnBI17A25VDrrBQ94NJ23DJarIxnG5YPjwJGKiG2iKKIymERIFvJVxMUCjn3giAUHT7uIbtf00DsMkBivd"
);
const appearance = {
  theme: "stripe",
};

const ReviewStack = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-xl);
`;
const LockedPanel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  background-color: var(--bg-200);
  border: 1px dashed var(--bg-300);
  padding: var(--spacing-md);
`;
const LockedBody = styled.div`
  flex: 1;
  min-width: 0;
`;
const LockIcon = styled.svg`
  flex-shrink: 0;
  margin-top: 2px;
`;
const ReviewSectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 var(--spacing-xs) 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-200);
`;
const AddressLabel = styled.p`
  margin: 0 0 4px 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-200);
`;
const AddressText = styled.p`
  margin: 0;
  font-size: 15px;
  color: var(--text-200);
`;

const PaymentSection = styled.div`
  margin-bottom: 32px;
  width: 100%;
  padding: 12px 0;
  border: 1px solid var(--bg-200);
  background-color: var(--bg-200);
  display: flex;
  align-items: center;
  justify-content: center;
`;
const PaymentWrapper = styled.div`
  width: 96%;
`;
const SectionTitle = styled.h3`
  font-weight: 600;
  margin-bottom: 16px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 16px;
  cursor: pointer;

  input {
    margin-right: 12px;
    accent-color: #0070f3;
  }

  &:hover {
    color: #0070f3;
  }
`;

const Step3 = ({ setPaymentMethod, address, shippingInfo, onSubmit }) => {
  const { t } = useTranslation();
  const { currencyTag } = useContext(ProductContext);
  const [clientSecret, setClientSecret] = useState("");
  const [paymentMethodLocal, setPaymentMethodLocal] = useState("cod");
  const cartItems = useSelector((state) => state.cart.itemsList);
  const subtotalRounded = parseFloat(
    cartItems
      .reduce(
        (total, item) => total + item.product.discount_price * item.quantity,
        0
      )
      .toFixed(2)
  );
  const shippingCost =
    shippingInfo?.name &&
    qualifiesForFreeShipping(subtotalRounded, shippingInfo.name)
      ? 0
      : shippingInfo?.price ?? 0;

  useEffect(() => {
    setPaymentMethod(paymentMethodLocal);
  }, [paymentMethodLocal, setPaymentMethod]);

  const selectedMethod = paymentMethods.find(
    (method) => method.code === paymentMethodLocal
  );

  return (
    <>
      <ReviewStack>
        {address && (
          <LockedPanel>
            <LockIcon width="14" height="14" viewBox="0 0 24 24" fill="none">
              <rect
                x="5"
                y="11"
                width="14"
                height="9"
                rx="1.5"
                stroke="var(--text-200)"
                strokeWidth="1.6"
              />
              <path
                d="M8 11V7.5C8 5.01472 9.79086 3 12 3C14.2091 3 16 5.01472 16 7.5V11"
                stroke="var(--text-200)"
                strokeWidth="1.6"
              />
            </LockIcon>
            <LockedBody>
              <ReviewSectionTitle>
                {t("CHECKOUT.DELIVER_TO")}
              </ReviewSectionTitle>
              <AddressLabel>{address.country}</AddressLabel>
              <AddressText>
                {`${address.street} ${address.street_number}, ${address.city} ${address.postal_code}`}
              </AddressText>
            </LockedBody>
          </LockedPanel>
        )}
        {shippingInfo?.name && (
          <LockedPanel>
            <LockIcon width="14" height="14" viewBox="0 0 24 24" fill="none">
              <rect
                x="5"
                y="11"
                width="14"
                height="9"
                rx="1.5"
                stroke="var(--text-200)"
                strokeWidth="1.6"
              />
              <path
                d="M8 11V7.5C8 5.01472 9.79086 3 12 3C14.2091 3 16 5.01472 16 7.5V11"
                stroke="var(--text-200)"
                strokeWidth="1.6"
              />
            </LockIcon>
            <LockedBody>
              <ReviewSectionTitle>
                {t("CHECKOUT.DELIVERY_OPTIONS")}
              </ReviewSectionTitle>
              <AddressLabel>{shippingInfo.name}</AddressLabel>
              <AddressText>
                {shippingCost === 0
                  ? t("CHECKOUT.FREE")
                  : `${currencyTag}${shippingCost.toFixed(2)}`}
              </AddressText>
            </LockedBody>
          </LockedPanel>
        )}
      </ReviewStack>

      <PaymentSection>
        <PaymentWrapper>
          <SectionTitle>{t("PAYMENT_METHOD.SELECT")}</SectionTitle>
          {paymentMethods.map((method) => (
            <RadioOption key={method.code}>
              <input
                type="radio"
                value={method.code}
                checked={paymentMethodLocal === method.code}
                onChange={() => setPaymentMethodLocal(method.code)}
                disabled={!method.available}
              />
              {t(`PAYMENT_METHOD.${method.code.toUpperCase()}`)}{" "}
              {!method.available && ` ${t("PAYMENT_METHOD.UNAVAILABLE")}`}
            </RadioOption>
          ))}
        </PaymentWrapper>
      </PaymentSection>

      {selectedMethod?.code === "card" && selectedMethod.available && (
        <>
          <CreditCardsCheckout
            address={address}
            setClientSecret={setClientSecret}
          />
          <CreditCardButton
            clientSecret={clientSecret}
            handleOrderSubmission={onSubmit}
          />
        </>
      )}
    </>
  );
};

export default Step3;
